/**
 * Accounting Document Form Module
 * Handles accounting document (Invoice / CreditNote) form initialization and interactions.
 * No delivery logic, no product-change handler. Item names are free-text inputs.
 */

import { CurrencyFormatter } from '../utils/currency-formatter';
import {
  CurrencyDataSource,
  CurrencyInfo,
  calculateItemTotals as sharedCalculateItemTotals,
  initSidebarNavigation,
} from './document-form-shared';

class AccountingDocumentFormManager {
  private itemsContainer: HTMLElement;
  private template: HTMLTemplateElement;
  private addButtons: NodeListOf<HTMLButtonElement>;
  private emptyState: HTMLElement | null;
  private bottomAddButtonWrapper: HTMLElement | null;
  private itemCounter = 0;
  private currencies: CurrencyDataSource = {};
  private currentCurrencySelect: HTMLSelectElement | null;
  private draggedItemRow: HTMLElement | null = null;

  constructor() {
    this.itemsContainer = document.getElementById('documentItemsContainer') as HTMLElement;
    this.template = document.querySelector('template') as HTMLTemplateElement;
    this.addButtons = document.querySelectorAll('.js-document-add-item-btn');
    this.emptyState = document.getElementById('docEmptyState');
    this.bottomAddButtonWrapper = document.getElementById('addDocumentItemBottomWrapper');
    this.currentCurrencySelect = document.querySelector('[name="CurrencyId"]') as HTMLSelectElement;

    if (!this.itemsContainer || !this.template || this.addButtons.length === 0) {
      throw new Error('Required accounting document form elements not found');
    }

    this.init();
  }

  private init(): void {
    this.loadCurrencyData();

    this.addButtons.forEach((button) => {
      button.addEventListener('click', () => this.addItem());
    });

    if (this.currentCurrencySelect) {
      this.currentCurrencySelect.addEventListener('change', () => this.onCurrencyChange());
    }

    this.initSortableItems();
    this.itemCounter = this.itemsContainer.querySelectorAll('.order-item-row').length;
    this.initializeExistingItems();
    this.syncItemOrders();
    this.updateAddButtonVisibility();
  }

  private initSortableItems(): void {
    this.itemsContainer.addEventListener('dragover', (event) => this.handleItemDragOver(event));
    this.itemsContainer.addEventListener('drop', (event) => this.handleItemDrop(event));
  }

  private loadCurrencyData(): void {
    const sectionItems = document.getElementById('section-items');
    if (sectionItems) {
      const currenciesData = sectionItems.getAttribute('data-currencies');
      if (currenciesData) {
        try {
          this.currencies = JSON.parse(currenciesData);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to parse currency data:', error);
        }
      }
    }
  }

  private getCurrentCurrency(): CurrencyInfo {
    const currencyId = this.currentCurrencySelect?.value || '1';
    return (
      this.currencies[currencyId] || {
        Code: 'CZK',
        Culture: 'cs-CZ',
        FreeDeliveryLimit: 0,
        ProductVatPercent: 0,
        DeliveryVatPercent: 0,
      }
    );
  }

  private formatPrice(amount: number): string {
    const currency = this.getCurrentCurrency();
    return CurrencyFormatter.formatPrice(amount, currency.Code, currency.Culture);
  }

  private addItem(): void {
    if (this.emptyState) {
      this.emptyState.style.display = 'none';
    }

    const clone = this.template.content.cloneNode(true) as DocumentFragment;
    const row = clone.querySelector('.order-item-row') as HTMLElement;

    const index = this.itemCounter++;
    row.innerHTML = row.innerHTML.replace(/INDEX/g, index.toString());

    this.itemsContainer.appendChild(row);

    // New items default to the currently selected currency's product VAT rate
    const vatInput = row.querySelector('.item-vat') as HTMLInputElement | null;
    if (vatInput) {
      vatInput.value = (this.getCurrentCurrency().ProductVatPercent ?? 0).toString();
    }

    this.attachItemEventListeners(row);
    this.syncItemOrders();
    this.updateAddButtonVisibility();
    this.updateSummary();
  }

  private initializeExistingItems(): void {
    const existingItems = this.itemsContainer.querySelectorAll('.order-item-row');
    existingItems.forEach((row) => {
      this.attachItemEventListeners(row as HTMLElement);
    });
  }

  private attachItemEventListeners(row: HTMLElement): void {
    const quantityInput = row.querySelector('.item-quantity') as HTMLInputElement;
    const priceInput = row.querySelector('.item-price') as HTMLInputElement;
    const vatInput = row.querySelector('.item-vat') as HTMLInputElement;
    const saleInput = row.querySelector('.item-sale') as HTMLInputElement;
    const weightInput = row.querySelector('.item-unit-weight') as HTMLInputElement;
    const removeBtn = row.querySelector('.remove-item-btn') as HTMLButtonElement;
    const dragHandle = row.querySelector('.js-item-drag-handle') as HTMLElement;

    quantityInput?.addEventListener('input', () => this.calculateRowTotals(row));
    priceInput?.addEventListener('input', () => this.calculateRowTotals(row));
    vatInput?.addEventListener('input', () => this.calculateRowTotals(row));
    saleInput?.addEventListener('input', () => this.calculateRowTotals(row));
    weightInput?.addEventListener('input', () => this.calculateRowTotals(row));
    removeBtn?.addEventListener('click', () => this.removeItem(row));
    dragHandle?.addEventListener('dragstart', (event) => this.handleItemDragStart(event, row));
    dragHandle?.addEventListener('dragend', () => this.resetDraggedItemState());

    this.calculateRowTotals(row);
  }

  private handleItemDragStart(event: DragEvent, row: HTMLElement): void {
    this.draggedItemRow = row;
    row.classList.add('is-dragging');

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', row.dataset.itemOrder ?? '');
    }
  }

  private handleItemDragOver(event: DragEvent): void {
    if (!this.draggedItemRow) {
      return;
    }

    event.preventDefault();

    const targetRow = (event.target as HTMLElement).closest(
      '.order-item-row'
    ) as HTMLElement | null;
    if (!targetRow || targetRow === this.draggedItemRow) {
      return;
    }

    const targetRect = targetRow.getBoundingClientRect();
    const insertAfter = event.clientY >= targetRect.top + targetRect.height / 2;
    const referenceNode = insertAfter ? targetRow.nextElementSibling : targetRow;

    this.itemsContainer.insertBefore(this.draggedItemRow, referenceNode);
  }

  private handleItemDrop(event: DragEvent): void {
    if (!this.draggedItemRow) {
      return;
    }

    event.preventDefault();
    this.syncItemOrders();
  }

  private resetDraggedItemState(): void {
    if (this.draggedItemRow) {
      this.draggedItemRow.classList.remove('is-dragging');
      this.draggedItemRow = null;
    }

    this.syncItemOrders();
  }

  private syncItemOrders(): void {
    const rows = this.itemsContainer.querySelectorAll<HTMLElement>('.order-item-row');

    rows.forEach((row, index) => {
      row.dataset.itemOrder = index.toString();

      const itemOrderInput = row.querySelector('.item-order-input') as HTMLInputElement | null;
      if (itemOrderInput) {
        itemOrderInput.value = index.toString();
      }

      // Re-index name, id, and for attributes so ASP.NET Core model binding receives
      // consecutive zero-based indices even after removes or reorders.
      row.querySelectorAll<HTMLElement>('[name], [id], [for]').forEach((el) => {
        const name = el.getAttribute('name');
        if (name) {
          el.setAttribute('name', name.replace(/Items\[\d+\]/, `Items[${index}]`));
        }
        const id = el.getAttribute('id');
        if (id) {
          el.setAttribute('id', id.replace(/Items_(\d+)__/, `Items_${index}__`));
        }
        const forAttr = el.getAttribute('for');
        if (forAttr) {
          el.setAttribute('for', forAttr.replace(/Items_(\d+)__/, `Items_${index}__`));
        }
      });
    });
  }

  private calculateRowTotals(row: HTMLElement): void {
    sharedCalculateItemTotals(row, (amount) => this.formatPrice(amount));
    this.calculatePricePerKg(row);
    this.updateSummary();
  }

  private calculatePricePerKg(row: HTMLElement): void {
    const priceInput = row.querySelector('.item-price') as HTMLInputElement;
    const weightInput = row.querySelector('.item-unit-weight') as HTMLInputElement;
    const quantityInput = row.querySelector('.item-quantity') as HTMLInputElement;
    const pricePerKgElement = row.querySelector('.item-price-per-kg') as HTMLElement;
    const totalWeightElement = row.querySelector('.item-total-weight') as HTMLElement;

    const pricePerUnit = parseFloat(priceInput?.value || '0');
    const weightGrams = parseFloat(weightInput?.value || '0');
    const quantity = parseFloat(quantityInput?.value || '0');

    if (pricePerKgElement) {
      if (weightGrams > 0) {
        const weightKg = weightGrams / 1000;
        const pricePerKg = pricePerUnit / weightKg;
        pricePerKgElement.textContent = this.formatPrice(pricePerKg);
      } else {
        pricePerKgElement.textContent = '—';
      }
    }

    if (totalWeightElement) {
      if (weightGrams > 0 && quantity > 0) {
        const totalKg = (weightGrams / 1000) * quantity;
        totalWeightElement.textContent = `${totalKg.toFixed(3)} kg`;
      } else {
        totalWeightElement.textContent = '—';
      }
    }
  }

  private removeItem(row: HTMLElement): void {
    row.remove();

    const remaining = this.itemsContainer.querySelectorAll('.order-item-row');
    if (remaining.length === 0 && this.emptyState) {
      this.emptyState.style.display = 'block';
    }

    this.syncItemOrders();
    this.updateAddButtonVisibility();
    this.updateSummary();
  }

  private updateAddButtonVisibility(): void {
    const hasItems = this.itemsContainer.querySelectorAll('.order-item-row').length > 0;
    this.bottomAddButtonWrapper?.classList.toggle('d-none', !hasItems);
  }

  private updateSummary(): void {
    const items = this.itemsContainer.querySelectorAll('.order-item-row');
    let totalWithoutVat = 0;
    let totalVat = 0;

    items.forEach((item) => {
      const quantityInput = item.querySelector('.item-quantity') as HTMLInputElement;
      const priceInput = item.querySelector('.item-price') as HTMLInputElement;
      const vatInput = item.querySelector('.item-vat') as HTMLInputElement;
      const saleInput = item.querySelector('.item-sale') as HTMLInputElement;

      const quantity = parseFloat(quantityInput?.value || '0');
      const pricePerUnit = parseFloat(priceInput?.value || '0');
      const vatPercent = parseFloat(vatInput?.value || '0');
      const salePercent = parseFloat(saleInput?.value || '0');

      const discountedPrice = pricePerUnit * (1 - salePercent / 100);
      const itemWithoutVat = quantity * discountedPrice;
      const itemVat = itemWithoutVat * (vatPercent / 100);

      totalWithoutVat += itemWithoutVat;
      totalVat += itemVat;
    });

    const totalPrice = totalWithoutVat + totalVat;

    const summaryWithoutVat = document.getElementById('docSummaryWithoutVat');
    const summaryVat = document.getElementById('docSummaryVat');
    const summaryTotal = document.getElementById('docSummaryTotal');
    const summarySection = document.getElementById('docSummary');

    if (summaryWithoutVat) summaryWithoutVat.textContent = this.formatPrice(totalWithoutVat);
    if (summaryVat) summaryVat.textContent = this.formatPrice(totalVat);
    if (summaryTotal) summaryTotal.textContent = this.formatPrice(totalPrice);

    if (summarySection) {
      summarySection.classList.toggle('d-none', items.length === 0);
    }
  }

  private onCurrencyChange(): void {
    const currency = this.getCurrentCurrency();

    // Re-prefill every item's VAT with the new currency's default
    const items = this.itemsContainer.querySelectorAll('.order-item-row');
    items.forEach((item) => {
      const vatInput = item.querySelector('.item-vat') as HTMLInputElement | null;
      if (vatInput) {
        vatInput.value = (currency.ProductVatPercent ?? 0).toString();
      }
      this.calculateRowTotals(item as HTMLElement);
    });
  }
}

/**
 * Initialize accounting document form functionality.
 * Only runs if .accounting-document-form-js element exists in the document.
 */
export async function initAccountingDocumentForm(
  container: Document | HTMLElement = document
): Promise<void> {
  if (!initAccountingDocumentForm.listenerInitialized) {
    document.addEventListener('form-replaced', () => {
      initAccountingDocumentForm(document);
    });
    initAccountingDocumentForm.listenerInitialized = true;
  }

  const docForm = container.querySelector('.accounting-document-form-js');

  if (!docForm) {
    return;
  }

  initSidebarNavigation();
  new AccountingDocumentFormManager();
}

// Static flag to track if event listener has been initialized
initAccountingDocumentForm.listenerInitialized = false;
